import { useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { usersAtom } from 'state/userState';
import {
  DialogTypes,
  ModelTypes,
  RoutePaths,
  OrderByDirection,
  GeneralIds,
} from 'types/types';
import { query } from 'operations';

import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Chip,
  IconButton,
  TablePagination,
  Typography,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { Check, Delete, Edit } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getDateTimeString, getRoutePath } from 'utils';
import { useTranslation } from 'react-i18next';
import { ToastSeverity, confirmDialogState, toastStateSelector } from 'state';
import { remove } from 'operations/remove';
import { Box } from '@mui/system';
import { TableHeaderCell } from 'components/TableHeaderCell';
import { User } from 'api/models';
import useHasAccess from 'hooks/useHasAccess';
import { Permission } from 'state/permissionState';
import i18next from 'i18next';
import useRequest from 'hooks/useRequest';

export default function Users() {
  const [users, setUserStore] = useRecoilState(usersAtom);
  const setConfirmDialog = useSetRecoilState(confirmDialogState);
  const { projectId } = useParams<GeneralIds>();
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [orderBy, setOrderBy] = useState('');
  const [orderByDirection, setOrderByDirection] = useState(
    OrderByDirection.asc,
  );
  const [totalCount, setTotalCount] = useState<number>(0);
  const setToast = useSetRecoilState(toastStateSelector);
  const { t } = useTranslation();
  const canEditUser = useHasAccess([Permission.WRITE_USER]);
  const navigate = useNavigate();

  useRequest(
    async () => {
      try {
        const { data: users, meta } = await query<User[]>({
          type: ModelTypes.user,
          ids: { projectId },
          queryParams: {
            pageSize,
            pageNumber,
            orderBy,
            orderByDirection,
          },
        });
        setTotalCount(meta.totalCount ?? 0);
        setUserStore(users);
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18next.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
        setUserStore([]);
      }
    },
    {
      dependencies: [pageSize, pageNumber, orderBy, orderByDirection],
    },
  );

  function onUserDeleteConfirm(userId: string) {
    return async () => {
      try {
        await remove({
          type: ModelTypes.user,
          ids: {
            id: userId,
            projectId: projectId,
          },
        });
        const { data: users } = await query<User[]>({
          type: ModelTypes.user,
          ids: { projectId },
          queryParams: {
            pageSize,
            pageNumber,
            orderBy,
            orderByDirection,
          },
        });

        setUserStore(users);
        setToast({
          isOpen: true,
          message: t('general.success.delete'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18next.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
      }
    };
  }

  const onDelete = (userId: string) => {
    setConfirmDialog({
      isOpen: true,
      dialogType: DialogTypes.confirmDelete,
      action: onUserDeleteConfirm(userId),
    });
  };

  const onOrderByClick = (orderBy: string) => {
    setOrderBy(orderBy);
  };

  const onDirectionClick = () => {
    setOrderByDirection(
      orderByDirection === OrderByDirection.asc
        ? OrderByDirection.desc
        : OrderByDirection.asc,
    );
  };

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableHeaderCell
                data-testid="users-table-header-email"
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                onOrderByClick={onOrderByClick}
                onDirectionClick={onDirectionClick}
                name="email"
              />
              <TableHeaderCell
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                onOrderByClick={onOrderByClick}
                onDirectionClick={onDirectionClick}
                name="name"
              />
              <TableHeaderCell
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                onOrderByClick={onOrderByClick}
                onDirectionClick={onDirectionClick}
                name="subProject"
                align="center"
              />

              <TableHeaderCell
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                onOrderByClick={onOrderByClick}
                onDirectionClick={onDirectionClick}
                name="permissions"
                align="center"
              />
              <TableHeaderCell
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                onOrderByClick={onOrderByClick}
                onDirectionClick={onDirectionClick}
                name="lastLogin"
                align="right"
              />
              <TableHeaderCell
                orderBy={orderBy}
                orderByDirection={orderByDirection}
                onOrderByClick={onOrderByClick}
                onDirectionClick={onDirectionClick}
                name="isSystemAdmin"
                align="center"
              />

              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                {t('general.operations')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography align="center">{t('general.noData')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user, ind) => (
                <TableRow
                  key={`users-table-${user.id}-row`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {user.email}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {user.lastName} {user.firstName}
                  </TableCell>

                  <TableCell align="center">
                    {user.subProjects?.map(subProject => (
                      <Chip
                        data-testid={`sub-project-${subProject.id}`}
                        key={`subproject-chip-${subProject.id}`}
                        label={subProject.name}
                        color="primary"
                      />
                    ))}
                  </TableCell>
                  <TableCell align="right">
                    {user.roles?.map(role => (
                      <Chip
                        data-testid={`user-roles-${role.id}`}
                        sx={{ m: 1 }}
                        key={`role-chip-${role.id}`}
                        label={role.name}
                      />
                    ))}
                  </TableCell>
                  <TableCell align="right">
                    {getDateTimeString(user.updatedAt)}
                  </TableCell>
                  <TableCell align="center">
                    {user.isSystemAdmin ? <Check /> : <Checkbox disabled />}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      disabled={!canEditUser || !user.subProjects}
                      onClick={() =>
                        navigate(
                          getRoutePath(RoutePaths.userEditor, {
                            userId: user.id.toString(),
                            projectId: projectId,
                          }),
                        )
                      }
                    >
                      <Tooltip title={t('general.edit')}>
                        <Edit fontSize="small" />
                      </Tooltip>
                    </IconButton>
                    <IconButton
                      onClick={() => onDelete(user.id)}
                      size="small"
                      disabled={!canEditUser || !user.subProjects}
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Tooltip title={t('general.delete')}>
                        <Delete fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* @ts-ignore is only needed because of the data-testid */}
      <TablePagination
        slotProps={{
          actions: {
            previousButton: {
              'data-testid': 'users-table-pagination-previous',
            },
            nextButton: {
              'data-testid': 'users-table-pagination-next',
            },
          },
        }}
        data-testid="users-table-pagination"
        rowsPerPageOptions={[3, 5, 10]}
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={pageNumber - 1}
        onPageChange={(_event, newPage) => setPageNumber(newPage + 1)}
        onRowsPerPageChange={event => setPageSize(+event.target.value)}
        labelRowsPerPage={t('general.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to}, ${count} ${t('general.user')}`
        }
      />
    </Box>
  );
}
